import React from 'react';
import { Outlet } from '@tanstack/react-router';
import {
  Buildings,
  User,
  LinkSimple,
  Tag,
  Robot,
  Gear,
  Envelope,
  Key,
  ChatText,
  ShieldCheck,
  FlowArrow,
  BookOpen,
  Cloud,
  ChartBar,
  CreditCard,
  Bell,
  Eye,
  Browser,
  Globe,
  IdentificationCard,
} from '@phosphor-icons/react';
import { SlackLogo, GithubLogo } from '@phosphor-icons/react';
import { useSubNav } from '../../hooks/useSubNav';
import type { SubNavConfig } from '../../contexts/SubNavContext';
import { useAuth } from '../../contexts/AuthContext';

export function SettingsLayout() {
  const { user } = useAuth();
  const settingsNavItems = React.useMemo<SubNavConfig['items']>(() => {
    const items: SubNavConfig['items'] = [
      {
        section: 'General',
        id: 'organization',
        label: 'Organization',
        icon: <Buildings size={16} />,
        to: '/settings',
      },
      {
        section: 'General',
        id: 'team',
        label: 'Team Members',
        icon: <User size={16} />,
        to: '/settings/team',
      },
      {
        section: 'General',
        id: 'security',
        label: 'Security',
        icon: <ShieldCheck size={16} />,
        to: '/settings/security',
      },
      {
        section: 'Integrations',
        id: 'jira',
        label: 'Jira Integration',
        icon: <LinkSimple size={16} />,
        to: '/settings/jira',
      },
    ];

    if (user?.features?.linear) {
      items.push({
        section: 'Integrations',
        id: 'linear',
        label: 'Linear',
        icon: <LinkSimple size={16} />,
        to: '/settings/linear',
      });
    }

    if (user?.features?.azureDevops) {
      items.push({
        section: 'Integrations',
        id: 'azure-devops',
        label: 'Azure DevOps',
        icon: <LinkSimple size={16} />,
        to: '/settings/azure-devops',
      });
    }

    if (user?.features?.aws) {
      items.push({
        section: 'Integrations',
        id: 'aws',
        label: 'AWS',
        icon: <Cloud size={16} />,
        to: '/settings/aws',
      });
    }

    items.push({
      section: 'Integrations',
      id: 'claude',
      label: 'Coding agents (MCP)',
      icon: <Robot size={16} />,
      to: '/settings/claude',
    });

    items.push(
      {
        section: 'Integrations',
        id: 'slack',
        label: 'Slack',
        icon: <SlackLogo size={16} />,
        to: '/settings/slack',
      },
      {
        section: 'Integrations',
        id: 'teams',
        label: 'Microsoft Teams',
        icon: <ChatText size={16} />,
        to: '/settings/teams',
      },
      {
        section: 'Integrations',
        id: 'github',
        label: 'GitHub',
        icon: <GithubLogo size={16} />,
        to: '/settings/github',
      },
      {
        section: 'Integrations',
        id: 'cloudflare-access',
        label: 'Cloudflare Access',
        icon: <ShieldCheck size={16} />,
        to: '/settings/cloudflare-access',
      },
      {
        section: 'Config',
        id: 'issue-categories',
        label: 'Issue Labels',
        icon: <Tag size={16} />,
        to: '/settings/issue-categories',
      },
      {
        section: 'Config',
        id: 'tags',
        label: 'Tags',
        icon: <Tag size={16} weight="fill" />,
        to: '/settings/tags',
      },
      {
        section: 'Config',
        id: 'workflows',
        label: 'Workflows',
        icon: <FlowArrow size={16} />,
        to: '/settings/workflows',
      },
      {
        section: 'Config',
        id: 'api-keys',
        label: 'API Keys',
        icon: <Key size={16} />,
        to: '/settings/api-keys',
      },
      ...(user?.features?.testSignIn
        ? [
            {
              section: 'Config',
              id: 'test-sign-in',
              label: 'Test sign-in',
              icon: <IdentificationCard size={16} />,
              to: '/settings/test-sign-in',
            },
          ]
        : []),
      {
        section: 'Config',
        id: 'documentation',
        label: 'Documentation',
        icon: <BookOpen size={16} />,
        to: '/settings/documentation',
      },
      {
        section: 'Config',
        id: 'observability',
        label: 'Observability',
        icon: <Eye size={16} />,
        to: '/settings/observability',
      },
      {
        section: 'Config',
        id: 'agent-browser',
        label: 'Agent Browser',
        icon: <Browser size={16} />,
        to: '/settings/agent-browser',
      },
      {
        section: 'General',
        id: 'usage',
        label: 'Usage',
        icon: <ChartBar size={16} />,
        to: '/settings/usage',
      },
    );

    if (user?.canViewBilling) {
      items.push({
        section: 'General',
        id: 'billing',
        label: 'Billing & usage',
        icon: <CreditCard size={16} />,
        to: '/settings/billing',
      });
    }

    items.push({
      section: 'Config',
      id: 'notifications',
      label: 'Notifications',
      icon: <Bell size={16} />,
      to: '/settings/notifications',
    });

    items.push({
      section: 'Config',
      id: 'digests',
      label: 'Digests',
      icon: <Envelope size={16} />,
      to: '/settings/digests',
    });

    if (user?.features?.sandboxCustomDomains) {
      items.push({
        section: 'Config',
        id: 'custom-domains',
        label: 'Custom Domains',
        icon: <Globe size={16} />,
        to: '/settings/custom-domains',
      });
    }

    if (user?.isSuperadmin) {
      items.push(
        {
          section: 'Superadmin',
          id: 'features',
          label: 'Feature Flags',
          icon: <Gear size={16} />,
          to: '/settings/features',
        },
        {
          section: 'Superadmin',
          id: 'inbox',
          label: 'Email Inbox',
          icon: <Envelope size={16} />,
          to: '/settings/inbox',
        }
      );
    }

    return items;
  }, [
    user?.isSuperadmin,
    user?.canViewBilling,
    user?.features?.linear,
    user?.features?.aws,
    user?.features?.sandboxCustomDomains,
    user?.features?.testSignIn,
  ]);

  const settingsNavConfig = React.useMemo<SubNavConfig>(() => {
    return {
      title: 'Settings',
      basePath: '/settings',
      items: settingsNavItems,
      getActiveId: ({ pathname }) => {
        if (!pathname.startsWith('/settings')) return undefined;
        const maybeSection = pathname.split('/')[2];
        const normalizedSection = maybeSection && maybeSection.length > 0 ? maybeSection : 'organization';
        const hasMatch = settingsNavItems.some((item) => item.id === normalizedSection);
        return hasMatch ? normalizedSection : 'organization';
      },
    };
  }, [settingsNavItems]);

  useSubNav(settingsNavConfig);

  return <Outlet />;
}
